<template>
  <v-container>
    <PageHeader noPadding title="Monthly Water Quality Report" icon="poll" />
    <v-progress-linear indeterminate v-if="globalIsLoading"></v-progress-linear>
    <v-alert type="info" :value="(!globalIsLoading) && (reportVisibilityOption.length == 0)">
      There are no report types activated for this tenant.
      Please contact the system administrator to activate reports.
    </v-alert>

    <template v-if="selectedReport === null && !globalIsLoading">
      <v-card v-if="reportTypeIsVisible(reportObjects.wqMonthly.configKey)">
        <v-card-title class="headline">
          Summary Report
        </v-card-title>
        <v-card-text class="pt-0">
          Summary report for surface water, process water and groundwater.
          <br />
          Contains most analytes.
        </v-card-text>
        <v-card-text>
          <v-layout>
            <v-flex sm6 md3>
              <v-select solo :items="monthlyReportSelectItems" v-model="selectedReportMonthYear" label="Select Month:" return-object single-line menu-props="bottom" item-text="text" :disabled="!monthlyReportSelectItems.length" :loading="isLoadingMonthYearList">
              </v-select>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn :loading="loadingDownloadMonthlyReport" color="info" @click="downloadMonthlyReport" :disabled="!selectedReportMonthYear.year">
            <v-icon class="mr-1">save_alt</v-icon>
            {{ $t('Button.Download') }}
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-if="reportTypeIsVisible(reportObjects.bcMoeMonthly.configKey)" class="mt-3">
        <v-card-title class="headline">
          BC MOE Summary Report
        </v-card-title>
        <v-card-text class="pt-0">
          BC MOE summary report for surface water, process water and groundwater.
          <br />
          Contains analytes required for discharge permit.
        </v-card-text>
        <v-card-text>
          <v-layout>
            <v-flex sm6 md3>
              <v-select solo v-bind:items="monthlyReportBCMoeSelectItems" v-model="selectedReportBCMoeMonthYear" label="Select Month:" return-object single-line menu-props="bottom" item-text="text" :disabled="!monthlyReportBCMoeSelectItems.length" :loading="isLoadingMonthYearList">
              </v-select>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn :loading="loadingDownloadBCMoeMonthlyReport" color="info" @click="downloadBCMoeMonthlyReport" :disabled="!selectedReportBCMoeMonthYear.year">
            <v-icon class="mr-1">save_alt</v-icon>
            {{ $t('Button.Download') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
    <WaterQualityReportsErrorModal v-model="errorModal" :data="errorModalData" :loading="errorModalLoading" @download="onErrorModalCallback" @cancel="onErrorModalClose" />
    <ErrorDialog v-model="errorDialog.show" :data="errorDialog.data" :title="errorDialog.title"></ErrorDialog>
  </v-container>
</template>

<script>
  import MomentUtil from '@/components/mixins/MomentUtil'
  import DownloadUtil from '@/components/mixins/DownloadUtil'
  import SnackBarUtil from '@/components/mixins/SnackBarUtil'
  import ErrorDialog from '@/components/generic/ErrorDialog'
  import ReportsErrorModalUtil from '@/components/mixins/ReportsErrorModalUtil'
  import moment from 'moment'
  import axios from 'axios'
  import WaterQualityReportsErrorModal from '@/components/WaterQualityReportsErrorModal'
  import {
    createNamespacedHelpers,
  } from 'vuex'
  import {
    UiConfigKeys,
  } from '@/store/UI/uiconfig'
  import {
    reportTypes,
  } from '@/store/stations'

  const authenticate = createNamespacedHelpers('authenticate')
  const stations = createNamespacedHelpers('stations')
  const uiConfig = createNamespacedHelpers('uiconfig')

  export default {
    components: {
      WaterQualityReportsErrorModal,
      ErrorDialog,
    },
    mixins: [MomentUtil, DownloadUtil, SnackBarUtil, ReportsErrorModalUtil],
    props: [],
    mounted() {
      this.fetchUiConfigOptions()
    },
    data() {
      return {
        selectedReportMonthYear: {},
        selectedReportBCMoeMonthYear: {},
        items: [],
        selectedReport: null,
        selectedStation: '',
        isLoadingMonthYearList: false,
        loadingDownloadMonthlyReport: false,
        loadingDownloadBCMoeMonthlyReport: false,
      }
    },
    computed: {
      ...authenticate.mapGetters(['currentUserGroup']),
      ...stations.mapGetters(['stationCategories']),
      ...uiConfig.mapGetters(['uiConfigOptions', 'uiConfigOptionByKey']),
      monthlyReportSelectItems() {
        if (this.items == null) {
          return []
        }
        return this.items.map(x => {
          return {
            text: x.format('MMM-YYYY'),
            month: x.month() + 1,
            year: x.year(),
          }
        })
      },
      monthlyReportBCMoeSelectItems() {
        if (this.items == null) {
          return []
        }
        return this.items.map(x => {
          return {
            text: x.format('MMM-YYYY'),
            month: x.month() + 1,
            year: x.year(),
          }
        })
      },
      globalIsLoading() {
        return this.uiConfigOptions.isLoading
      },
      reportObjects() {
        return reportTypes
      },
      reportVisibilityOption() {
        let data = this.uiConfigOptionByKey(
          UiConfigKeys.ReportComponentVisibility
        )
        if (!(data && data.Value)) {
          return []
        }
        return JSON.parse(data.Value)
      },
      errorDialog() {
        return this.$store.getters['app/errorDialog']
      },
    },
    methods: {
      ...uiConfig.mapActions(['getUiConfigOptions']),
      async downloadMonthlyReport({
        suppressErrors,
      }) {
        this.showDownloadSnackbarMessage()
        this.loadingDownloadMonthlyReport = true
        let url = (
          '/Report/Monthly?downloadMonth=' +
          this.selectedReportMonthYear.month +
          '&downloadYear=' +
          this.selectedReportMonthYear.year)

        let fileName = (
          'Water_Quality_Monthly_Report_' +
          this.selectedReportMonthYear.text +
          '.xlsx')
        let params = {
          suppressErrors: suppressErrors || false,
        }
        let options = {
          params: params,
        }
        try {
          await this.download(url, fileName, options)
          this.resetErrorModal()
        } catch (error) {
          this.progress1 = false
          let { data, customTitle } = this.generateReportTitleAndData(error.response.data)
          this.setErrorModal(data, () => {
            this.downloadMonthlyReport({
              suppressErrors: true,
            })
          }, customTitle)
        } finally {
          this.loadingDownloadMonthlyReport = false
        }
      },
      // async downloadBCMoeMonthlyReport({
      //   // suppressErrors,
      // }) {
        // this.showDownloadSnackbarMessage()
        // this.loadingDownloadBCMoeMonthlyReport = true
        // var url = (
        //   '/Report/Template?downloadMonth=' +
        //   this.selectedReportBCMoeMonthYear.month +
        //   '&downloadYear=' +
        //   this.selectedReportBCMoeMonthYear.year +
        //   '&quickReport=true')
        // var fileName =
        //   'Water_Quality_Monthly_Report_BCMOE_' +
        //   this.selectedReportBCMoeMonthYear.text +
        //   '.xlsx'
        // let params = {
        //   suppressErrors: suppressErrors || false,
        // }
        // var options = {
        //   params: params,
        //   successful: () => {
        //     this.progress2 = false
        //     this.resetErrorModal()
        //   },
        //   failedCb: error => {
        //     this.progress2 = false
        //     let { data, customTitle } = this.generateReportTitleAndData(error.response.data)
        //     this.setErrorModal(data, () => {
        //       this.downloadBCMoeMonthlyReport({
        //         suppressErrors: true,
        //       })
        //     }, customTitle)
        //   },
        // }
      // },
      showDownloadSnackbarMessage() {
        this.handleNotification({
          type: 'info',
          message: 'The download may take some time to finish. Please do not close your browser until the download is complete.',
          timeout: 8000,
        })
      },
      reportTypeIsVisible(reportTypeConfigKey) {
        return this.reportVisibilityOption.includes(reportTypeConfigKey)
      },
      fetchUiConfigOptions() {
        let that = this
        let options = {
          config: {},
          forced: false,
          successCb() {
            that.fetchAvailableMonths()
          },
          failedCb: (msg) => {
            console.error(msg)
            this.$store.dispatch('app/setErrorDialog', {
              data: `failed to fetch report visibility options: ${msg}`,
              title: msg.message,
              show: true,
            })
          },
        }
        this.getUiConfigOptions(options)
      },
      fetchAvailableMonths() {
        // let that = this
        if (this.reportVisibilityOption == []) {
          return
        }
        this.isLoadingMonthYearList = true
        axios
          .get(
            '/api/WaterQuality/AvailableMonths/' +
            this.currentUserGroup.name
          )
          .then(res => {
            this.isLoadingMonthYearList = false
            this.items = res.data.map(x => moment(x))
          })
          .catch(err => {
            this.isLoadingMonthYearList = false
            console.error(err)
            this.$store.dispatch('app/setErrorDialog', {
              data: `failed to fetch report availability ${err}`,
              title: err.message,
              show: true,
            })
          })
      },
    },
  }
</script>

<style lang="stylus" scoped>
  .toolbar__title.display-2 {
    padding-bottom: 10px;
    margin-left: 10px !important;
  }

  .v-progress-circular {
    margin-left: 1rem;
  }
</style>